import type { BusinessGetResponseType } from "@sellar-io/api-v2/types";
import { useQuery } from "@tanstack/react-query";
import type { ExtendedFirebaseUser } from "../../../useFirebaseUser.shared";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import {
  apiV2AssertResponse,
  ApiV2QueryOptions,
  apiV2Request,
  getDefaultQueryOptionsV2,
} from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";

type SCOPE = "scope:edit-business-details";

export const getBusinessQueryKey = ({
  userId,
  businessId,
  scope,
}: {
  userId: string | undefined;
  businessId: number | undefined;
  scope?: SCOPE;
}) => [userId, businessId, ServerStateKeys.Businesses, scope] as const;

const useBusinessQuery = <TData = BusinessGetResponseType,>({
  user,
  currentBusiness,
  businessId,
  options,
  scope,
}: {
  user: ExtendedFirebaseUser | undefined;
  currentBusiness: BusinessGetResponseType | null;
  businessId?: number;
  options?: ApiV2QueryOptions<TData>;
  scope?: SCOPE;
}) =>
  useQuery({
    queryKey: getBusinessQueryKey({
      userId: user?.uid,
      businessId: currentBusiness?.id ?? businessId,
      scope,
    }),
    queryFn: () =>
      apiV2Request({
        user,
        business: currentBusiness,
        method: "get",
        path: `businesses/${businessId}`,
      }).then((response) => apiV2AssertResponse<TData>(response).data),
    ...getDefaultQueryOptionsV2({
      user,
      business: currentBusiness,
      overrideOptions: {
        ...options,
        enabled: Boolean(businessId) && (options?.enabled ?? true),
        requireUser: true,
        // Business access is authorized by ID without existing business context.
        requireBusiness: false,
      },
    }),
  });

/**
 * @param scope - This helps to narrow down the specific query on the client
 */
export const useBusiness = <TData = BusinessGetResponseType,>(
  businessId?: number,
  options?: ApiV2QueryOptions<TData>,
  scope?: SCOPE,
) => {
  const currentBusinessContext = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();

  return useBusinessQuery({
    user,
    currentBusiness: currentBusinessContext.business,
    businessId,
    options,
    scope,
  });
};

/**
 * Observes the business cache seeded by /auth/session. Refetch only after
 * invalidation, so settings updates stay live without another bootstrap read.
 */
export const useSessionBusiness = (
  user: ExtendedFirebaseUser | undefined,
  businessId?: number,
) =>
  useBusinessQuery({
    user,
    currentBusiness: null,
    businessId,
    options: { retry: false, staleTime: Infinity },
  });
